import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
const useVisibleCount = () => {
  const [visible, setVisible] = useState(3);
  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1024) setVisible(3);else if (window.innerWidth >= 640) setVisible(2);else setVisible(1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return visible;
};
export const Reviews = () => {
  const {
    t
  } = useLanguage();
  const visible = useVisibleCount();
  const reviews = [1, 2, 3, 4, 5].map(n => ({
    text: t(`reviews.${n}.text`),
    author: t(`reviews.${n}.author`),
    location: t(`reviews.${n}.location`)
  }));
  // Duplicate so the track can advance one full set, then snap back invisibly.
  const track = [...reviews, ...reviews];
  // step is continuous (0..reviews.length). When it reaches reviews.length, we snap back to 0.
  const [step, setStep] = useState(0);
  const [animate, setAnimate] = useState(true);
  const pausedRef = useRef(false);
  // Auto-advance
  useEffect(() => {
    const id = setInterval(() => {
      if (!pausedRef.current) setStep(p => p + 1);
    }, 4500);
    return () => clearInterval(id);
  }, []);
  // After the wrap-around animation lands at step = reviews.length, snap to 0 silently.
  const handleComplete = () => {
    if (step >= reviews.length) {
      setAnimate(false);
      setStep(0);
      // re-enable animation on the next frame
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
    }
  };
  const next = () => {
    pausedRef.current = true;
    setStep(p => p + 1);
    window.setTimeout(() => pausedRef.current = false, 6000);
  };
  const prev = () => {
    pausedRef.current = true;
    if (step === 0) {
      // jump to the equivalent duplicate position then animate back
      setAnimate(false);
      setStep(reviews.length);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setAnimate(true);
        setStep(reviews.length - 1);
      }));
    } else {
      setStep(p => p - 1);
    }
    window.setTimeout(() => pausedRef.current = false, 6000);
  };
  const goTo = (i: number) => {
    pausedRef.current = true;
    setStep(i);
    window.setTimeout(() => pausedRef.current = false, 6000);
  };
  // Card width %: on mobile (visible=1) make 88% so the next card peeks
  const cardBasis = visible === 1 ? '85%' : `calc((100% - ${visible - 1} * 1rem) / ${visible})`;
  // Shift in percent of container width.
  // For visible=1 with peek, each step = 85% + 1rem gap ≈ 85% + (1rem / containerWidth)*100%.
  // We approximate using just the percentage (gap is small relative to mobile width).
  const shiftPct = visible === 1 ? 85 * step : 100 / visible * step;
  const activeDot = step % reviews.length;
  return <section id="resenas" className="py-10 sm:py-16 md:py-20 bg-[#0A1F44] relative overflow-hidden" data-id="element-528" data-mp="h5pohb55y7ri">
      <div className="absolute top-10 -left-20 w-72 h-72 sm:w-80 sm:h-80 bg-turquoise/10 rounded-full blur-3xl" data-id="element-529" data-mp="c8pgguor6uxr" />
      <div className="absolute bottom-10 -right-20 w-72 h-72 sm:w-80 sm:h-80 bg-gold/10 rounded-full blur-3xl" data-id="element-530" data-mp="qxvcyaw7i71y" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-531" data-mp="m3hdnxefbfs0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-6 sm:mb-12" data-id="element-532" data-mp="zvns9f1xhepc">
          <span className="inline-block bg-turquoise/15 text-turquoise px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3" data-id="element-533" data-mp="3bm9m2ijwmb0w">
            {t('reviews.subtitle')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight" data-id="element-534" data-mp="385djlh1vsga7">
            {t('reviews.title')}
          </h2>
          <div className="w-16 h-1 bg-turquoise mx-auto rounded-full" data-id="element-535" data-mp="3d5rsf1bb3u9a" />
        </motion.div>

        {/* Carousel */}
        <div className="relative" data-id="element-536" data-mp="3n1urtzerxesj">
          {/* Edge fades only on md+ */}
          <div className="hidden md:block absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none bg-gradient-to-r from-[#0A1F44] to-transparent" data-id="element-537" data-mp="3mzs4xo8x41s1" />
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none bg-gradient-to-l from-[#0A1F44] to-transparent" data-id="element-538" data-mp="2uce259ijgc00" />

          <div className="overflow-hidden -mx-4 sm:mx-0 px-4 sm:px-0" onTouchStart={() => pausedRef.current = true} onTouchEnd={() => window.setTimeout(() => pausedRef.current = false, 6000)} data-id="element-539" data-mp="2zaflzwnsgfcj">
            <motion.div className="flex gap-3 sm:gap-4 pt-3" animate={{
            x: `-${shiftPct}%`
          }} transition={animate ? {
            type: 'spring',
            stiffness: 90,
            damping: 22
          } : {
            duration: 0
          }} onAnimationComplete={handleComplete} data-id="element-540" data-mp="2bl4vd805k1c4">
              {track.map((review, i) => <div key={i} style={{
              flex: `0 0 ${cardBasis}`
            }} data-id="element-541" data-mp="51qvcm7dzxxu" data-mpi={i}>
                  <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg relative h-full" data-id="element-542" data-mp="y18q2a9vqfg">
                    <Quote className="absolute -top-3 left-4 sm:left-5 w-6 h-6 sm:w-7 sm:h-7 text-turquoise bg-white rounded-full p-1 sm:p-1.5 shadow" data-id="element-543" data-mp="3s4lyliolcsaw" />

                    <div className="flex space-x-0.5 mb-3 pt-2" data-id="element-544" data-mp="3i9k6cdz0ra43">
                      {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 text-gold fill-gold" data-id="element-545" data-mp="38jbyhgb6dtfj" data-mpi={s} />)}
                    </div>

                    <p className="text-[13px] sm:text-sm text-navy leading-relaxed mb-4 line-clamp-5 sm:line-clamp-4" data-id="element-546" data-mp="2zm1khn6z3q6x">
                      "{review.text}"
                    </p>

                    <div className="flex items-center space-x-2.5 pt-3 border-t border-gray-100" data-id="element-547" data-mp="3rcpnl4s7j5n8">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-turquoise to-navy flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0" data-id="element-548" data-mp="3saowt9x37v">
                        {review.author.charAt(0)}
                      </div>
                      <div className="min-w-0" data-id="element-549" data-mp="e4cpzizzcgwm">
                        <div className="font-bold text-navy text-sm leading-tight truncate" data-id="element-550" data-mp="e3l798rhednr">
                          {review.author}
                        </div>
                        <div className="text-xs text-gray-500 truncate" data-id="element-551" data-mp="23kyjrojlglhl">
                          {review.location}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
            </motion.div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-center items-center gap-3 sm:gap-4 mt-6 sm:mt-8" data-id="element-552" data-mp="28etc1gd9m8sa">
          <button onClick={prev} aria-label="Previous review" className="w-11 h-11 rounded-full bg-white/10 hover:bg-turquoise hover:text-navy active:bg-turquoise active:text-navy text-white flex items-center justify-center transition-all backdrop-blur-sm border border-white/10" data-id="element-553" data-mp="1knntgtfxx2ea">
            <ChevronLeft className="w-5 h-5" data-id="element-554" data-mp="1pola31laxodo" />
          </button>

          <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-555" data-mp="35yxghdepcrm7">
            {reviews.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Go to review ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === activeDot ? 'w-6 sm:w-7 bg-turquoise' : 'w-1.5 bg-white/30'}`} data-id="element-556" data-mp="31147ydpxflir" data-mpi={i} />)}
          </div>

          <button onClick={next} aria-label="Next review" className="w-11 h-11 rounded-full bg-white/10 hover:bg-turquoise hover:text-navy active:bg-turquoise active:text-navy text-white flex items-center justify-center transition-all backdrop-blur-sm border border-white/10" data-id="element-557" data-mp="18b3c2vqvygvq">
            <ChevronRight className="w-5 h-5" data-id="element-558" data-mp="1r2w7sxxcag75" />
          </button>
        </div>
      </div>
    </section>;
};